class PvPdpGallery extends HTMLElement {
  #abortController = null;
  #scrollFrame = null;
  #activeIndex = 0;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    this.track = this.querySelector('[data-pv-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-pv-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-pv-gallery-thumb]'));
    this.current = this.querySelector('[data-pv-gallery-current]');
    this.dialog = this.querySelector('[data-pv-zoom-dialog]');
    this.zoomImage = this.querySelector('[data-pv-zoom-image]');

    if (!this.track || this.slides.length === 0) return;

    this.track.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    this.track.addEventListener('keydown', this.#onTrackKeydown, { signal });

    this.querySelectorAll('[data-pv-gallery-go]').forEach((button) => {
      button.addEventListener('click', this.#onGoClick, { signal });
    });

    this.querySelectorAll('[data-pv-zoom-open]').forEach((button) => {
      button.addEventListener('click', this.#openZoom, { signal });
    });

    this.querySelector('[data-pv-zoom-close]')?.addEventListener('click', () => this.dialog?.close(), { signal });
    this.dialog?.addEventListener('click', this.#closeOnBackdrop, { signal });
    this.#setActive(0, false);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = null;
  }

  goTo(index, smooth = true) {
    const normalized = Math.max(0, Math.min(index, this.slides.length - 1));
    const target = this.slides[normalized];
    if (!target || !this.track) return;
    this.track.scrollTo({ left: target.offsetLeft, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
    this.#setActive(normalized, smooth);
  }

  #onScroll = () => {
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      if (!this.track?.clientWidth) return;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      this.#setActive(index, true);
    });
  };

  #onGoClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    const direction = button.dataset.pvGalleryGo;
    const requested = Number(button.dataset.pvGalleryIndex);
    if (Number.isFinite(requested)) return this.goTo(requested);
    if (direction === 'prev') this.goTo(Math.max(0, this.#activeIndex - 1));
    if (direction === 'next') this.goTo(Math.min(this.slides.length - 1, this.#activeIndex + 1));
  };

  #onTrackKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(Math.max(0, this.#activeIndex - 1));
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(Math.min(this.slides.length - 1, this.#activeIndex + 1));
    }
  };

  #setActive(index, scrollThumb) {
    const normalized = Math.max(0, Math.min(index, this.slides.length - 1));
    this.#activeIndex = normalized;
    if (this.current) this.current.textContent = String(normalized + 1);

    const previous = this.querySelector('[data-pv-gallery-go="prev"]');
    const next = this.querySelector('[data-pv-gallery-go="next"]');
    if (previous instanceof HTMLButtonElement) previous.disabled = normalized === 0;
    if (next instanceof HTMLButtonElement) next.disabled = normalized === this.slides.length - 1;
    this.thumbs.forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-current', thumbIndex === normalized ? 'true' : 'false');
    });
    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === normalized;
      slide.toggleAttribute('inert', !isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      const zoomButton = slide.querySelector('[data-pv-zoom-open]');
      if (zoomButton instanceof HTMLButtonElement) zoomButton.tabIndex = isActive ? 0 : -1;
    });
    if (scrollThumb) {
      const thumb = this.thumbs[normalized];
      const navigation = thumb?.parentElement;
      if (thumb && navigation && navigation.scrollWidth > navigation.clientWidth) {
        const centeredLeft = thumb.offsetLeft - ((navigation.clientWidth - thumb.offsetWidth) / 2);
        navigation.scrollTo({
          left: Math.max(0, centeredLeft),
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }
    }
  }

  #openZoom = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement) || !this.dialog || !this.zoomImage) return;
    this.zoomImage.src = button.dataset.pvZoomSrc || '';
    this.zoomImage.alt = button.dataset.pvZoomAlt || '';
    this.dialog.showModal();
  };

  #closeOnBackdrop = (event) => {
    if (event.target === this.dialog) this.dialog.close();
  };
}

class PvPdp extends HTMLElement {
  #abortController = null;
  #ctaObserver = null;
  #stickyFrame = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    this.variantSelect = this.querySelector('[data-pv-pdp-variant]');
    this.variantInput = this.querySelector('[data-pv-pdp-variant-id]');
    this.mainButton = this.querySelector('.pv-pdp__cta[ref="addToCartButton"]');
    this.mobilePriceAnchor = this.querySelector('.pv-pdp__mobile-commercial');
    this.sticky = this.querySelector('[data-pv-pdp-sticky]');
    this.gallery = this.querySelector('pv-pdp-gallery');

    this.variantSelect?.addEventListener('change', this.#onVariantChange, { signal });
    this.sticky?.querySelector('button')?.addEventListener('click', this.#triggerMainCta, { signal });
    this.#setupSticky();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#ctaObserver?.disconnect();
    this.#ctaObserver = null;
    if (this.#stickyFrame) cancelAnimationFrame(this.#stickyFrame);
    this.#stickyFrame = null;
    document.documentElement.classList.remove('pv-pdp-sticky-visible');
  }

  #setupSticky() {
    if (!this.mainButton || !this.sticky) return;
    this.#ctaObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      this.#updateStickyState();
    }, { threshold: 0.01 });
    this.#ctaObserver.observe(this.mainButton);
    if (this.mobilePriceAnchor) this.#ctaObserver.observe(this.mobilePriceAnchor);

    const signal = this.#abortController.signal;
    window.addEventListener('scroll', this.#scheduleStickyUpdate, { signal, passive: true });
    window.addEventListener('resize', this.#scheduleStickyUpdate, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', this.#scheduleStickyUpdate, { signal, passive: true });
    this.#updateStickyState();
  }

  #scheduleStickyUpdate = () => {
    if (this.#stickyFrame) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = null;
      this.#updateStickyState();
    });
  };

  #updateStickyState() {
    if (!this.mainButton || !this.sticky) return;
    const mobileAnchor = window.matchMedia('(max-width: 749px)').matches ? this.mobilePriceAnchor : null;
    const triggerRect = (mobileAnchor || this.mainButton).getBoundingClientRect();
    const passed = triggerRect.bottom < 0;
    const viewport = window.visualViewport;
    const keyboardOpen = viewport && window.innerHeight - viewport.height > 180 && /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '');
    const visible = passed && !keyboardOpen && !this.sticky.classList.contains('prevura-sticky-bar--suppressed');
    this.sticky.dataset.visible = visible ? 'true' : 'false';
    document.documentElement.classList.toggle('pv-pdp-sticky-visible', visible);
    this.sticky.toggleAttribute('inert', !visible);
    if (visible) this.sticky.removeAttribute('aria-hidden');
    else this.sticky.setAttribute('aria-hidden', 'true');
  }

  #triggerMainCta = () => {
    if (!this.mainButton || this.mainButton.disabled) return;
    this.mainButton.dataset.puppet = 'true';
    try {
      this.mainButton.click();
    } finally {
      this.mainButton.dataset.puppet = 'false';
    }
  };

  #onVariantChange = (event) => {
    const select = event.currentTarget;
    if (!(select instanceof HTMLSelectElement)) return;
    const option = select.options[select.selectedIndex];
    if (!option || !this.variantInput) return;

    this.variantInput.value = option.value;
    const available = option.dataset.available === 'true';
    const price = option.dataset.price || '';
    const priceAmount = Number(option.dataset.priceAmount);
    const compare = option.dataset.compare || '';
    const saving = option.dataset.saving || '';
    const discount = option.dataset.discount || '';
    const mediaIndex = Number(option.dataset.mediaIndex);
    const quantityInput = this.querySelector('[data-pv-pdp-quantity]');
    const availabilityTitle = this.querySelector('[data-pv-pdp-availability-title]');
    const availabilityText = this.querySelector('[data-pv-pdp-availability-text]');
    const mainLabel = this.mainButton?.querySelector('.add-to-cart-text__content > span > span');
    const stickyButton = this.sticky?.querySelector('[data-pv-pdp-action-button]');
    const stickyOffer = this.sticky?.querySelector('[data-pv-pdp-sticky-offer]');
    const productMedia = this.querySelector('add-to-cart-component');

    this.querySelectorAll('[data-pv-pdp-price]').forEach((element) => { element.textContent = price; });
    this.querySelectorAll('[data-pv-pdp-compare]').forEach((element) => {
      element.textContent = compare;
      element.hidden = !compare;
    });
    this.querySelectorAll('[data-pv-pdp-saving]').forEach((element) => {
      element.textContent = discount ? `${discount} % Rabatt` : '';
      element.hidden = !saving;
    });
    if (stickyOffer) stickyOffer.textContent = discount ? `${discount} % Rabatt` : stickyOffer.dataset.fallback || '';
    this.querySelectorAll('.pv-pdp__cta, .pv-pdp__sticky-button').forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = !available;
    });

    if (availabilityTitle) {
      availabilityTitle.textContent = available
        ? availabilityTitle.dataset.availableTitle || 'Auf Lager'
        : availabilityTitle.dataset.unavailableTitle || 'Derzeit nicht verfügbar';
    }
    if (availabilityText) {
      availabilityText.textContent = available
        ? availabilityText.dataset.availableText || ''
        : availabilityText.dataset.unavailableText || 'Diese Auswahl kann momentan nicht bestellt werden.';
    }
    if (mainLabel) mainLabel.textContent = available ? this.dataset.availableLabel || 'In den Warenkorb' : this.dataset.soldOutLabel || 'Ausverkauft';
    if (stickyButton) stickyButton.textContent = available ? stickyButton.dataset.availableLabel || 'Sichern' : stickyButton.dataset.soldOutLabel || 'Ausverkauft';
    if (productMedia instanceof HTMLElement) productMedia.dataset.productVariantMedia = option.dataset.mediaUrl || '';

    if (quantityInput instanceof HTMLInputElement) {
      const minimum = Math.max(1, Number(option.dataset.quantityMin) || 1);
      const increment = Math.max(1, Number(option.dataset.quantityStep) || 1);
      const maximum = Number(option.dataset.quantityMax);
      quantityInput.min = String(minimum);
      quantityInput.step = String(increment);
      quantityInput.dataset.cartQuantity = option.dataset.cartQuantity || '0';
      if (Number.isFinite(maximum) && maximum > 0) quantityInput.max = String(maximum);
      else quantityInput.removeAttribute('max');
      const requestedQuantity = Math.max(minimum, Number(quantityInput.value) || minimum);
      quantityInput.value = String(Number.isFinite(maximum) && maximum > 0 ? Math.min(requestedQuantity, maximum) : requestedQuantity);
    }

    if (Number.isFinite(mediaIndex) && mediaIndex >= 0) this.gallery?.goTo(mediaIndex);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    history.replaceState({}, '', url.toString());
    this.dispatchEvent(new CustomEvent('pv:pdp:variant-change', {
      bubbles: true,
      detail: {
        productId: this.dataset.productId || '',
        variantId: option.value,
        price,
        priceAmount,
        available,
        mediaUrl: option.dataset.mediaUrl || '',
      },
    }));
  };
}

class PvCoursePath extends HTMLElement {
  #abortController = null;
  #scrollFrame = null;
  #activeIndex = 0;
  #demoObserver = null;
  #demoTimers = [];
  #demoFinished = false;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    this.track = this.querySelector('[data-course-track]');
    this.cards = Array.from(this.querySelectorAll('[data-course-card]'));
    this.targets = Array.from(this.querySelectorAll('[data-course-target]'));
    this.current = this.querySelector('[data-course-current]');
    this.progress = this.querySelector('[data-course-progress]');
    this.previous = this.querySelector('[data-course-prev]');
    this.next = this.querySelector('[data-course-next]');
    this.flips = Array.from(this.querySelectorAll('[data-course-flip]'));
    this.dots = Array.from(this.querySelectorAll('[data-course-dot]'));

    if (!this.track || this.cards.length === 0) return;

    this.track.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    this.track.addEventListener('keydown', this.#onKeydown, { signal });
    this.track.addEventListener('pointerdown', this.#cancelDemo, { signal, passive: true });
    this.previous?.addEventListener('click', () => { this.#cancelDemo(); this.goTo(this.#activeIndex - 1); }, { signal });
    this.next?.addEventListener('click', () => { this.#cancelDemo(); this.goTo(this.#activeIndex + 1); }, { signal });
    this.targets.forEach((button, index) => button.addEventListener('click', () => { this.#cancelDemo(); this.goTo(index); }, { signal }));
    this.dots.forEach((dot, index) => {
      if (!(dot instanceof HTMLButtonElement)) return;
      dot.addEventListener('click', () => { this.#cancelDemo(); this.goTo(index); }, { signal });
    });
    this.flips.forEach((button, index) => button.addEventListener('click', () => { this.#cancelDemo(); this.#toggleCard(index); }, { signal }));
    this.#setActive(0);
    this.#setupDemo();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#demoObserver?.disconnect();
    this.#demoObserver = null;
    this.#clearDemoTimers();
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = null;
  }

  goTo(index) {
    const normalized = Math.max(0, Math.min(index, this.cards.length - 1));
    const target = this.cards[normalized];
    if (!target || !this.track) return;
    if (this.track.scrollWidth > this.track.clientWidth + 2) {
      const left = target.offsetLeft - ((this.track.clientWidth - target.clientWidth) / 2);
      this.track.scrollTo({
        left,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    } else {
      target.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }
    this.#setActive(normalized);
  }

  #onScroll = () => {
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = null;
      const trackCenter = this.track.scrollLeft + (this.track.clientWidth / 2);
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + (card.clientWidth / 2);
        const distance = Math.abs(cardCenter - trackCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      this.#setActive(closestIndex);
    });
  };

  #onKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.#cancelDemo();
    const focusedIndex = this.flips.indexOf(event.target);
    const currentIndex = focusedIndex >= 0 ? focusedIndex : this.#activeIndex;
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = Math.max(0, Math.min(currentIndex + direction, this.cards.length - 1));
    this.goTo(nextIndex);
    if (focusedIndex >= 0) this.flips[nextIndex]?.focus({ preventScroll: true });
  };

  #setActive(index) {
    const normalized = Math.max(0, Math.min(index, this.cards.length - 1));
    this.#activeIndex = normalized;
    if (this.current) this.current.textContent = `Woche ${normalized + 1} von ${this.cards.length}`;
    if (this.progress) {
      const progress = this.cards.length > 1 ? (normalized / (this.cards.length - 1)) * 100 : 100;
      this.progress.style.width = `${progress}%`;
    }
    this.cards.forEach((card, cardIndex) => { card.dataset.active = cardIndex === normalized ? 'true' : 'false'; });
    this.targets.forEach((button, buttonIndex) => {
      if (buttonIndex === normalized) {
        button.setAttribute('aria-current', 'step');
        button.dataset.state = 'current';
      } else {
        button.removeAttribute('aria-current');
        button.dataset.state = buttonIndex < normalized ? 'done' : 'upcoming';
      }
    });
    this.dots.forEach((button, buttonIndex) => {
      if (buttonIndex === normalized) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
    if (this.previous instanceof HTMLButtonElement) this.previous.disabled = normalized === 0;
    if (this.next instanceof HTMLButtonElement) this.next.disabled = normalized === this.cards.length - 1;
  }

  #toggleCard(index) {
    const selectedCard = this.cards[index];
    if (!selectedCard) return;
    const shouldFlip = selectedCard.dataset.flipped !== 'true';
    this.#setCardFlip(index, shouldFlip);
    this.#setActive(index);
  }

  #setCardFlip(index, shouldFlip) {
    this.cards.forEach((card, cardIndex) => {
      const flipped = cardIndex === index && shouldFlip;
      card.dataset.flipped = flipped ? 'true' : 'false';
      const button = this.flips[cardIndex];
      if (button instanceof HTMLButtonElement) {
        button.setAttribute('aria-pressed', flipped ? 'true' : 'false');
        const front = card.querySelector('.pv-pdp-course-path__face--front');
        const back = card.querySelector('.pv-pdp-course-path__face--back');
        front?.setAttribute('aria-hidden', flipped ? 'true' : 'false');
        back?.setAttribute('aria-hidden', flipped ? 'false' : 'true');
      }
    });
  }

  #setupDemo() {
    if (this.#demoFinished || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const demoTarget = this.cards[0] || this;
    this.#demoObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry?.isIntersecting || entry.intersectionRatio < 0.7) return;
      this.#demoObserver?.disconnect();
      this.#demoObserver = null;
      const openTimer = window.setTimeout(() => {
        if (this.#demoFinished) return;
        this.#setCardFlip(this.#activeIndex, true);
      }, 650);
      const closeTimer = window.setTimeout(() => {
        if (this.#demoFinished) return;
        this.#setCardFlip(this.#activeIndex, false);
        this.#demoFinished = true;
        this.#clearDemoTimers();
      }, 2200);
      this.#demoTimers.push(openTimer, closeTimer);
    }, { threshold: [0.7] });
    this.#demoObserver.observe(demoTarget);
  }

  #clearDemoTimers() {
    this.#demoTimers.forEach((timer) => window.clearTimeout(timer));
    this.#demoTimers = [];
  }

  #cancelDemo = () => {
    if (this.#demoFinished) return;
    this.#demoFinished = true;
    this.#demoObserver?.disconnect();
    this.#demoObserver = null;
    this.#clearDemoTimers();
  }
}

class PvPackageSystem extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    this.tabs = Array.from(this.querySelectorAll('[data-package-target]'));
    this.panels = Array.from(this.querySelectorAll('[data-package-panel]'));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#select(index), { signal });
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + this.tabs.length) % this.tabs.length;
        this.#select(nextIndex);
        this.tabs[nextIndex]?.focus();
      }, { signal });
    });
    this.#select(0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #select(index) {
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    this.panels.forEach((panel, panelIndex) => {
      panel.dataset.active = panelIndex === index ? 'true' : 'false';
    });
  }
}

class PvPillowRole extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    this.tabs = Array.from(this.querySelectorAll('[data-pillow-target]'));
    this.panels = Array.from(this.querySelectorAll('[data-pillow-panel]'));
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#select(index), { signal });
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (index + direction + this.tabs.length) % this.tabs.length;
        this.#select(nextIndex);
        this.tabs[nextIndex]?.focus();
      }, { signal });
    });
    this.#select(0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #select(index) {
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    this.panels.forEach((panel, panelIndex) => {
      panel.dataset.active = panelIndex === index ? 'true' : 'false';
    });
  }
}

if (!customElements.get('pv-pdp-gallery')) customElements.define('pv-pdp-gallery', PvPdpGallery);
if (!customElements.get('pv-pdp')) customElements.define('pv-pdp', PvPdp);
if (!customElements.get('pv-course-path')) customElements.define('pv-course-path', PvCoursePath);
if (!customElements.get('pv-package-system')) customElements.define('pv-package-system', PvPackageSystem);
if (!customElements.get('pv-pillow-role')) customElements.define('pv-pillow-role', PvPillowRole);

